import type {
  ResolvedApiBinding,
  TableQueryFilter,
  TableQueryRequest,
  TableQueryResult,
} from '@finance/contracts';
import type { TableDataSource } from '../../components/experience/table/table-data-source.js';
import { listResolvedResource } from './experience-api';

function pageRequest(request: TableQueryRequest): { page: number; pageSize: number } {
  if (!('page' in request.pagination)) {
    throw new Error('GENERIC_RESOURCE_CURSOR_PAGINATION_UNSUPPORTED');
  }
  return {
    // Table state is zero-based; the generic API contract is one-based.
    page: request.pagination.page + 1,
    pageSize: request.pagination.pageSize,
  };
}

function filters(
  request: TableQueryRequest,
  requiredFilters: Readonly<Record<string, unknown>>
): TableQueryFilter[] | undefined {
  if (request.filters.length === 0 && Object.keys(requiredFilters).length === 0) return undefined;

  // Do not trust or forward browser-provided serverKey aliases. The API derives
  // server fields from the authenticated table descriptor. Route restrictions
  // replace user filters for the same field so table controls can never weaken
  // a route-owned scope predicate.
  const requiredFields = new Set(Object.keys(requiredFilters));
  const result: TableQueryFilter[] = request.filters
    .filter(filter => !requiredFields.has(filter.field))
    .map(filter => ({
      field: filter.field,
      operator: filter.operator,
      value: filter.value,
    }));

  for (const [field, value] of Object.entries(requiredFilters)) {
    result.push({ field, operator: 'equals', value });
  }
  return result;
}

/**
 * Adapts one backend-resolved generic list binding to the shared server table
 * engine. The descriptor controls which fields and operators can reach this
 * function; cursor semantics fail closed because the generic resource contract
 * is offset-paginated. Grouping and aggregates are transported to, and
 * calculated by, the backend so a browser page can never synthesize totals.
 */
export function createGenericResourceDataSource(
  binding: ResolvedApiBinding,
  requiredFilters: Readonly<Record<string, unknown>> = {}
): TableDataSource {
  return {
    async fetch(request: TableQueryRequest, signal?: AbortSignal): Promise<TableQueryResult> {
      const pagination = pageRequest(request);
      const sort = request.sort.find(item => item.direction !== 'none');
      const response = await listResolvedResource<Record<string, unknown>>(
        binding,
        {
          ...pagination,
          search: request.globalSearch?.query || undefined,
          sortField: sort?.field,
          sortDirection: sort?.direction === 'none' ? undefined : sort?.direction,
          filters: filters(request, requiredFilters),
          grouping: request.grouping,
          aggregations: request.aggregations,
        },
        signal
      );

      const minimumKnownTotal = (pagination.page - 1) * pagination.pageSize + response.items.length;
      const pageInfo = response.pageInfo;
      const total = pageInfo?.total ?? minimumKnownTotal + (pageInfo?.hasNextPage ? 1 : 0);

      return {
        rows: [...response.items],
        pagination: {
          page: pagination.page - 1,
          pageSize: pagination.pageSize,
          total,
        },
        aggregates: response.aggregates ? { ...response.aggregates } : undefined,
        groupMetadata: response.groupMetadata ? { ...response.groupMetadata } : undefined,
        dataTimestamp: Date.now(),
      };
    },
  };
}
